import * as React from 'react';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, CircularProgress, Typography } from '@material-ui/core';
import { CommunicationChat } from '../components/CommunicationChat';
import { ClientData, CommunicationData, CommunicationStatus, CommunicationType, GoogleReviewState, GoogleReviewSummary, IncomingMessageNotification } from '../models/api-models';
import { getClientCommunicationsAsync, markAllClientCommunicationsAsReadAsync, requestClientGoogleReviewAsync, sendClientCommunicationAsync, setClientMintuPauseAsync, updateClientCommunicationObservationsAsync } from '../ajax/requests';
import { useTranslations } from '../store/translations/hooks';
import { TK } from '../store/translations/translationKeys';
import { useSelector, useDispatch } from 'react-redux';
import { alertError, alertSuccess } from '../store/alerts/actions';
import { loadIncomingMessagesSummary, markMessageAsRead } from '../store/general/actions';
import { ApplicationState, AppThunkDispatch } from '../store';
import { websocketService } from '../services/websocketService';
import { entitiesLoaded } from '../store/entities/actions';
import { CommunicationEntity } from '../store/entities/state';
import { getErrorMessage } from '../ajax/errors';
import { FeatureEnum } from '../models';
import { hasFeature } from '../store/session/selectors';

export interface ClientCommunicationsProps {
  client: ClientData;
  isOpen?: boolean;
}

export const ClientCommunications: React.FC<ClientCommunicationsProps> = ({ client, isOpen = true }) => {
  const t = useTranslations();
  const dispatch = useDispatch<AppThunkDispatch>();
  const hasArtificialIntelligence = useSelector(hasFeature(FeatureEnum.ArtificialInteligence));
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [latestGoogleReview, setLatestGoogleReview] = useState<GoogleReviewSummary | undefined>(undefined);
  const sendingRef = React.useRef(false);

  const clientId = client.id;
  const [mintuPausedUntil, setMintuPausedUntil] = useState<string | undefined>(client.mintuPausedUntil);

  useEffect(() => {
    setMintuPausedUntil(client.mintuPausedUntil);
  }, [client.mintuPausedUntil]);

  useEffect(() => {
    setLatestGoogleReview(undefined);
  }, [clientId]);

  // Get communications from Redux store filtered by clientId
  const allCommunications = useSelector((state: ApplicationState) => state.entities.communications.items);
  const communications = useMemo(() => {
    return Object.values(allCommunications)
      .filter((c): c is CommunicationEntity => c !== undefined && c.clientId === clientId);
  }, [allCommunications, clientId]);
  const activeClientIdRef = React.useRef<string | undefined>(clientId);
  const communicationIdsByClientRef = React.useRef<Record<string, string[]>>({});

  useEffect(() => {
    activeClientIdRef.current = clientId;
  }, [clientId]);

  useEffect(() => {
    if (!clientId) return;
    communicationIdsByClientRef.current[clientId] = communications.map(c => c.id);
  }, [clientId, communications]);

  const refreshCommunicationsSnapshot = useCallback(async (targetClientId: string) => {
    const previousClientCommunicationIds = communicationIdsByClientRef.current[targetClientId] ?? [];
    const data = await getClientCommunicationsAsync(targetClientId, 500, 0);

    if (activeClientIdRef.current !== targetClientId) {
      return;
    }

    const snapshot = data.messages as CommunicationEntity[];
    const snapshotIds = new Set(snapshot.map(c => c.id));
    const staleClientIds = previousClientCommunicationIds.filter(id => !snapshotIds.has(id));

    setLatestGoogleReview(data.latestGoogleReview);
    dispatch(entitiesLoaded('communications', snapshot, staleClientIds, 'AppendResult'));
  }, [dispatch]);

  const loadCommunications = useCallback(async () => {
    if (!clientId) return;

    try {
      setLoading(true);
      setError(null);
      await refreshCommunicationsSnapshot(clientId);
    } catch (err) {
      console.error('Failed to load communications:', err);
      setError(t(TK.somethingWentWrong));
    } finally {
      setLoading(false);
    }
  }, [clientId, refreshCommunicationsSnapshot, t]);

  useEffect(() => {
    loadCommunications();
  }, [loadCommunications]);

  useEffect(() => {
    if (!isOpen || !clientId) {
      return;
    }

    const markConversationAsRead = async () => {
      try {
        await markAllClientCommunicationsAsReadAsync(clientId);
        dispatch(loadIncomingMessagesSummary());
      } catch (err) {
        console.error('Failed to mark client communications as read:', err);
      }
    };

    markConversationAsRead();
  }, [clientId, dispatch, isOpen]);

  // Subscribe to real-time incoming messages via SignalR
  useEffect(() => {
    const handleIncomingMessage = (notification: IncomingMessageNotification) => {
      // If the incoming message is for this client, reload communications
      if (notification.clientId === clientId) {
        loadCommunications();
        // Mark the new message as read immediately since the chat is open
        dispatch(markMessageAsRead(clientId, notification.id, true));
      }
    };

    websocketService.onIncomingCommunicationReceived(handleIncomingMessage);

    // Cleanup function to remove the handler when component unmounts
    return () => {
      websocketService.onIncomingCommunicationReceived(() => {});
    };
  }, [clientId, dispatch, loadCommunications]);

  const handleSendMessage = useCallback(async (
    message: string,
    type: CommunicationType,
    subject?: string,
    attachments?: File[],
    conversationId?: string,
    observations?: string,
  ): Promise<boolean> => {
    if (sendingRef.current) return false;

    try {
      sendingRef.current = true;
      setSending(true);

      const sentCommunication = await sendClientCommunicationAsync(
        clientId,
        type,
        message,
        subject,
        attachments,
        conversationId,
        observations
      );

      if (type === CommunicationType.Email) {
        try {
          await refreshCommunicationsSnapshot(clientId);
        } catch (refreshErr) {
          console.error('Failed to refresh communications after email send:', refreshErr);
          const fallbackCommunication: CommunicationEntity = {
            ...(sentCommunication as CommunicationEntity),
            status: CommunicationStatus.Sent,
            statusUpdateTimestamp: sentCommunication.statusUpdateTimestamp ?? sentCommunication.timestamp,
          };
          dispatch(entitiesLoaded('communications', [fallbackCommunication], [], 'AppendResult'));
        }
      } else {
        dispatch(entitiesLoaded('communications', [sentCommunication as CommunicationEntity], [], 'AppendResult'));
      }

      // Show success message
      dispatch(alertSuccess(t(TK.sentSuccessfully)));

      return true;
    } catch (err: any) {
      console.error('Failed to send communication:', err);

      if (err == null) {
        return false;
      }

      const errorMessage = getErrorMessage(err) || t(TK.somethingWentWrong);
      dispatch(alertError(errorMessage));

      return false;
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  }, [clientId, dispatch, refreshCommunicationsSnapshot, t]);

  const handleUpdateObservations = useCallback(async (message: CommunicationData, observations?: string) => {
    try {
      const updated = await updateClientCommunicationObservationsAsync(clientId, message.id, observations);
      dispatch(entitiesLoaded('communications', [updated as CommunicationEntity], [], 'AppendResult'));
      dispatch(alertSuccess(t(TK.savedSuccessfully)));
      return true;
    } catch (err: any) {
      console.error('Failed to update communication observations:', err);
      const errorMessage = getErrorMessage(err) || t(TK.somethingWentWrong);
      dispatch(alertError(errorMessage));
      return false;
    }
  }, [clientId, dispatch, t]);

  const handleMintuPauseChange = useCallback(async (pauseHours?: number) => {
    try {
      const result = await setClientMintuPauseAsync(clientId, pauseHours);
      setMintuPausedUntil(result.mintuPausedUntil);
    } catch (err) {
      console.error('Failed to set Mintu pause:', err);
      dispatch(alertError(t(TK.somethingWentWrong)));
    }
  }, [clientId, dispatch, t]);

  const handleRequestGoogleReview = useCallback(async (channel: CommunicationType, branchId?: string) => {
    try {
      const sentCommunication = await requestClientGoogleReviewAsync(clientId, channel, branchId);
      dispatch(entitiesLoaded('communications', [sentCommunication as CommunicationEntity], [], 'AppendResult'));
      setLatestGoogleReview({
        sentAt: sentCommunication.timestamp,
        channel,
        state: GoogleReviewState.Pending,
      });
      dispatch(alertSuccess(t(TK.requestGoogleReviewSuccess)));
    } catch (err: any) {
      console.error('Failed to request Google review:', err);
      const errorMessage = getErrorMessage(err) || t(TK.somethingWentWrong);
      dispatch(alertError(errorMessage));
    }
  }, [clientId, dispatch, t]);

  const handleToggleRead = useCallback((message: CommunicationData, isRead: boolean) => {
    dispatch(markMessageAsRead(message.clientId!, message.id, isRead));
    // Update the communication in Redux store
    const updatedMessage = { ...message, isRead } as CommunicationEntity;
    dispatch(entitiesLoaded('communications', [updatedMessage], [], 'AppendResult'));
  }, [dispatch]);
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <CommunicationChat
        messages={communications}
        sendMessage={handleSendMessage}
        onToggleRead={handleToggleRead}
        onRefresh={loadCommunications}
        disabled={sending}
        isOpen={isOpen}
        clientName={client.name}
        clientPhoneNumbers={client.phoneNumbers}
        onUpdateObservations={handleUpdateObservations}
        mintuPausedUntil={hasArtificialIntelligence ? mintuPausedUntil : undefined}
        onMintuPauseChange={hasArtificialIntelligence ? handleMintuPauseChange : undefined}
        onRequestGoogleReview={handleRequestGoogleReview}
        googleReviewSummary={latestGoogleReview}
      />
    </Box>
  );
};
